import { apiClient } from './client';

export type BackupImportMode = 'Restore' | 'TestEnvironment';

export interface BackupPreview {
  tags: number;
  flows: number;
  flowTags: number;
  flowRevisions: number;
  envVars: number;
  oauthApps: number;
  tokenStores: number;
  users: number;
  adminUsers: number;
  apiKeys: number;
  syncStates: number;
  flowExecutions: number;
  stepExecutions: number;
}

export interface BackupExportRequest {
  passphrase: string;
  includeExecutions: boolean;
  executionDays: number;
  failedOnly: boolean;
}

export interface BackupWipeResponse {
  totalRowsDeleted: number;
  deletedByTable: Record<string, number>;
}

export interface BackupImportResponse {
  imported: BackupPreview;
  mode: BackupImportMode;
  overwrote: boolean;
  backupSchemaVersion: string;
  currentSchemaVersion: string;
  schemaVersionMatches: boolean;
  backupCreatedAt: string;
}

export const getBackupPreview = () =>
  apiClient.get<BackupPreview>('/api/admin/backup/preview').then(r => r.data);

export const exportBackup = (req: BackupExportRequest) =>
  apiClient
    .post('/api/admin/backup/export', req, { responseType: 'blob' })
    .then(r => ({ blob: r.data as Blob, filename: filenameFrom(r.headers['content-disposition']) }))
    .catch(async err => {
      // responseType 'blob' applies to error bodies too, so the server's message
      // arrives as a Blob and has to be read back out before it can be shown.
      const body = err?.response?.data;
      if (body instanceof Blob) throw new Error((await body.text()) || 'Export failed.');
      throw err;
    });

export const importBackup = (
  file: File,
  passphrase: string,
  mode: BackupImportMode,
  overwrite: boolean
) => {
  const form = new FormData();
  form.append('file', file);
  form.append('passphrase', passphrase);
  form.append('mode', mode);
  form.append('overwrite', String(overwrite));
  return apiClient
    .post<BackupImportResponse>('/api/admin/backup/import', form)
    .then(r => r.data);
};

/** Deletes everything except the calling admin. */
export const wipeInstance = () =>
  apiClient
    .post<BackupWipeResponse>('/api/admin/backup/wipe', { confirmation: 'DELETE' })
    .then(r => r.data);

function filenameFrom(contentDisposition: unknown): string {
  const fallback = 'flowforge-backup.ffbak';
  if (typeof contentDisposition !== 'string') return fallback;
  const match = /filename="?([^";]+)"?/.exec(contentDisposition);
  return match ? match[1] : fallback;
}
